import type { CrmNoteType } from '@plato/data/prisma';
import {
    differenceInCalendarDays,
    format,
    formatISO,
    intlFormat,
    isToday,
    min,
    parseISO,
    roundToNearestMinutes,
} from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { BanIcon, CalendarIcon, CalendarPlus, ClockIcon, NotebookPen, PaperclipIcon, X } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Controller, useController, useForm } from 'react-hook-form';
import { CustomerIcon } from '../../../assets/icons/customer-icon.tsx';
import { Button } from '../../../components/ui/button.tsx';
import { DatePicker } from '../../../components/ui/date-picker.tsx';
import { LinkText } from '../../../components/ui/link-text.tsx';
import Text from '../../../components/ui/text.tsx';
import TimePicker from '../../../components/ui/time-picker.tsx';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useVoiceRecording } from '../../../lib/contexts/voice-recording-context.tsx';
import { useDiscardChangesWarning } from '../../../lib/hooks/use-discard-changes-warning.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { appendPlainTextToLexical } from '../../../lib/rich-text/append-plain-text-to-lexical.ts';
import { cn } from '../../../lib/utils.ts';
import type { CrmEventDTO } from '../../../server/dto/crm-event-dto.ts';
import type { CrmTaskDTO } from '../../../server/dto/crm-task-dto.ts';
import type { CustomerDTO } from '../../../server/dto/customer-dto.ts';
import type { CrmTaskUpdateWithId } from '../../../server/queries/crm-log-interaction-create.ts';
import type { RouterInput } from '../../../server/types/infer-trpc-types.ts';
import { api } from '../../../utils/api.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { AnalyticsEventPayload } from '../../analytics/types/analytics-events.ts';
import { FileCard } from '../../crm-attachment/components/file-card.tsx';
import { useFileUploader } from '../../crm-attachment/hooks/use-file-uploader.tsx';
import { DialogForm } from '../../dialog/dialog-form.tsx';
import { DialogStateManager, useDialog } from '../../dialog/dialog-provider.tsx';
import { Dialog } from '../../dialog/dialog.tsx';
import type { ModalInnerProps } from '../../modal/index.tsx';
import type { AIActionType } from '../../rich-text/hooks/use-improve-writing.ts';
import { CustomerSelect } from '../../shared/components/form-items-with-fetch/customer-select.tsx';
import { VoiceRecordingButton } from '../../voice-recording/components/voice-recording-button.tsx';
import { LogInteractionTaskList } from './log-interaction-task-list.tsx';
import { CreateNoteForm } from './note-form-dialog.tsx';
import { NoteFormFields } from './note-form-fields.tsx';
import { NoteTypeSelector } from './note-type-selector.tsx';

export type LogInteractionFormProps = {
    customer?: CustomerDTO;
    event?: CrmEventDTO;
    onCancel?: () => void;
    onSuccess?: () => void;
    onIsDirtyChange?: (isDirty: boolean) => void;
    analyticsSource?: AnalyticsEventPayload<'customer_interaction_created'>['page_source'];
};

const getMaxDate = (): Date => roundToNearestMinutes(new Date(), { roundingMethod: 'floor', nearestTo: 15 });

const getDefaultOccurredAt = (event: CrmEventDTO | undefined): string => {
    if (!event) {
        return getMaxDate().toISOString();
    }

    return new Date(`${event.date}T${event.start_time ?? '12:00:00'}`).toISOString();
};

const getOccurredAtFromEvent = (event: CrmEventDTO): string =>
    fromZonedTime(`${event.date}T${event.start_time ?? '12:00:00'}`, 'Europe/Berlin').toISOString();

// Cross-fades between two variants sharing one slot. Both stay in flow; the inactive
// one collapses to zero height via a `grid-template-rows` 1fr -> 0fr transition, and
// the inner `overflow-hidden` clips its content as it shrinks, so the slot height
// animates smoothly between variants instead of snapping.
//
// `subgrid` threads the parent form's label/value columns through both layers (via
// `grid-cols-subgrid`) so their columns line up with the rest of the form. Collapsing
// height is what lets us keep the rows in flow instead of using `absolute`, which
// would break subgrid and snap the value sideways (and avoids `!important` fighting
// DialogForm.Item's baked-in subgrid). Without `subgrid` the layers are plain
// full-width blocks.
const SwapSlot = ({
    active,
    subgrid = false,
    transition = 'duration-300 ease-in-out',
    children,
}: {
    active: boolean;
    subgrid?: boolean;
    transition?: string;
    children: ReactNode;
}): ReactNode => (
    <div
        inert={!active}
        className={cn(
            'grid transition-[grid-template-rows,opacity]',
            transition,
            subgrid && 'col-span-2 grid-cols-subgrid',
            active ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0 pointer-events-none',
        )}
    >
        <div className={cn('overflow-hidden', subgrid && 'col-span-2 grid grid-cols-subgrid gap-3')}>
            {children}
        </div>
    </div>
);

export const LogInteractionForm = ({
    customer,
    event,
    onSuccess,
    onCancel,
    onIsDirtyChange,
    analyticsSource,
}: LogInteractionFormProps): ReactNode => {
    const { t, locale } = useLocale();
    const { toast } = useToast();
    const utils = api.useUtils();
    const { currentSessionId } = useVoiceRecording();
    const { enableFileUpload, enableCreateNextEventFakeDoor } = useAppConfig();
    const [noteType, setNoteType] = useState<CrmNoteType>('VISIT');

    const sendEvent = useAnalytics();
    const [isAIBusy, setIsAIBusy] = useState<boolean>(false);
    const [aiSessionId, setAISessionId] = useState<string | undefined>(undefined);
    const [suggestionDismissed, setSuggestionDismissed] = useState(false);

    const handleNoteTypeChange = (value: CrmNoteType): void => {
        setNoteType(value);
        setValue('interactionHappened', value === 'NOTE' ? null : true, { shouldDirty: true });
    };

    const dismissSuggestion = (): void => {
        setSuggestionDismissed(true);
        setValue('occurredAt', '', { shouldDirty: true });
        setValue('eventId', undefined, { shouldDirty: true });
    };
    const reapplySuggestion = (): void => {
        setSuggestionDismissed(false);
    };

    const applyNow = (): void => {
        setValue('occurredAt', getMaxDate().toISOString(), { shouldDirty: true });
    };
    const handleAIInteraction = (action: AIActionType, sessionId: string): void => {
        switch (action) {
            case 'accept':
                setAISessionId(sessionId);
                setIsAIBusy(false);
                break;
            case 'request':
            case 'regenerate':
                setIsAIBusy(true);
                break;
            case 'reject':
                setIsAIBusy(false);
                break;
            default:
                break;
        }
    };

    const defaultValues = {
        title: undefined,
        customerId: customer?.customerId,
        eventId: event?.event_id,
        occurredAt: getDefaultOccurredAt(event),
        attachments: undefined,
        interactionHappened: true,
        taskUpdates: [],
    };

    const {
        control,
        handleSubmit,
        clearErrors,
        watch,
        setValue,
        getValues,
        formState: { errors: formErrors, isDirty },
    } = useForm<
        Omit<RouterInput['platoUser']['command']['notes']['create'], 'noteType'> & {
            taskUpdates: CrmTaskUpdateWithId[];
        }
    >({
        defaultValues,
    });

    const attachmentsField = useController({ name: 'attachments', control });
    const fileUploader = useFileUploader({
        value: attachmentsField.field.value ?? [],
        onChange: attachmentsField.field.onChange,
        analyticsContext: 'note',
    });

    const watchedTaskUpdates = watch('taskUpdates');

    const handleToggleCompleted = (task: CrmTaskDTO, completed: boolean): void => {
        setValue(
            'taskUpdates',
            completed
                ? [
                      ...watchedTaskUpdates,
                      {
                          taskId: task.taskId,
                          description: task.descriptionRich,
                          assignedToId: task.assignedTo.id,
                          dueDate: task.dueDate ?? new Date(),
                          status: 'COMPLETED',
                          links: task.links.map((link) => ({
                              modelType: link.modelType,
                              modelId: link.modelId,
                              title: link.title,
                          })),
                      },
                  ]
                : watchedTaskUpdates.filter((u) => u.taskId !== task.taskId),
            { shouldDirty: true },
        );
    };

    const watchedCustomerId = watch('customerId');
    const watchedInteractionHappened = watch('interactionHappened');
    const notReachedMode = watchedInteractionHappened === false;
    const shouldSuggestDate = !event && !!watchedCustomerId && noteType !== 'NOTE';

    const lastEventQuery = api.platoUser.query.events.getLastUnloggedCustomerEvent.useQuery(
        {
            customerId: watchedCustomerId,
        },
        { enabled: shouldSuggestDate },
    );

    const upcomingTasksQuery = api.platoUser.query.tasks.getManyTasks.getUpcomingCustomerTasks.useQuery(
        { customerId: watchedCustomerId },
        { enabled: !!watchedCustomerId },
    );

    const suggestedEvent = shouldSuggestDate ? (lastEventQuery.data ?? null) : null;

    useEffect(() => {
        setSuggestionDismissed(false);
    }, [watchedCustomerId]);

    const showSuggestedEvent = !event && !!suggestedEvent && !suggestionDismissed;
    const eventDateTime = (event?: CrmEventDTO): string => {
        if (!event) {
            return '';
        }

        return intlFormat(
            new Date(getOccurredAtFromEvent(event)),
            {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            },
            { locale },
        );
    };

    useEffect(() => {
        clearErrors('title');
    }, [clearErrors]);

    useEffect(() => {
        onIsDirtyChange?.(isDirty);
    }, [isDirty, onIsDirtyChange]);

    useEffect(() => {
        if (!shouldSuggestDate || !lastEventQuery.isFetched || suggestedEvent) {
            return;
        }

        sendEvent('log_date_prefill_unavailable', { customer_id: watchedCustomerId });
    }, [shouldSuggestDate, lastEventQuery.isFetched, suggestedEvent, sendEvent, watchedCustomerId]);

    // While the suggestion is the active choice, mirror it into the form so submit
    // links the suggested event (eventId) and uses its date/time (occurredAt)
    useEffect(() => {
        if (!showSuggestedEvent) {
            return;
        }
        setValue('eventId', suggestedEvent.event_id);
        setValue('occurredAt', getOccurredAtFromEvent(suggestedEvent));
    }, [showSuggestedEvent, suggestedEvent, setValue]);

    const createMutation = api.platoUser.command.logInteraction.create.useMutation({
        onSuccess: async (note) => {
            await utils.platoUser.query.customerActivity.getFeedList.withFilters.invalidate();
            await utils.platoUser.query.events.getLastUnloggedCustomerEvent.invalidate();
            await utils.platoUser.query.tasks.getManyTasks.getUpcomingCustomerTasks.invalidate();
            await utils.platoUser.query.tasks.getManyTasks.getListWithFilters.invalidate();

            toast({
                icon: 'success',
                variant: 'default',
                title: t('notes__form_create_toast__success_title', { ns: 'notes' }),
                createdLink: <LinkText href={`/customers/${note.customerId}/activity`} />,
            });

            sendEvent('customer_interaction_created', {
                note_id: note.noteId,
                customer_id: note.customerId,
                note_type: noteType,
                ai_session_id: aiSessionId,
                page_source: analyticsSource,
                not_reached_mode: notReachedMode,
                had_reason: !!note.title,
            });

            if (suggestedEvent) {
                const daysAgo = differenceInCalendarDays(new Date(), parseISO(suggestedEvent.date));

                if (suggestionDismissed) {
                    sendEvent('log_date_prefill_overridden', {
                        event_id: suggestedEvent.event_id,
                        days_ago: daysAgo,
                        fields_changed: 'both',
                    });
                } else {
                    sendEvent('log_date_prefill_used', {
                        event_id: suggestedEvent.event_id,
                        days_ago: daysAgo,
                        interaction_type: 'besuch',
                    });
                }
            }

            onSuccess?.();
        },
        onError: (error) => {
            toast({
                icon: 'warning',
                title: 'Error',
                description: error.message,
            });
        },
    });

    const onSubmit = handleSubmit((data) => {
        const isNote = noteType === 'NOTE';
        createMutation.mutate({
            customerId: data.customerId,
            eventId: isNote ? undefined : data.eventId,
            noteType,
            crmNote: {
                ...data,
                titleRich: data.title,
                occurredAt: isNote ? undefined : data.occurredAt,
                interactionHappened: isNote ? null : data.interactionHappened,
            },
            taskUpdates: isNote ? [] : data.taskUpdates,
        });
    });

    const handleCreateNextEventClick = (): void => {
        sendEvent('create_next_event_from_log', {
            customer_id: watchedCustomerId,
        });
        toast({
            icon: 'info',
            title: t('fake_door_next_event_toast_title', { ns: 'events' }),
            description: t('fake_door_next_event_toast_description', { ns: 'events' }),
        });
    };

    return (
        <DialogForm>
            <div className="border-b border-neutral-200 px-5 py-4">
                <div className="mx-auto flex w-full max-w-2xl items-center">
                    <Text size="xl" weight="semibold" color="neutral-900">
                        {t('notes__form_create_title', { ns: 'notes' })}
                    </Text>
                </div>
            </div>
            <DialogForm.Content className="w-full sm:max-w-2xl sm:mx-auto overflow-y-auto min-h-[500px]">
                {!event && <NoteTypeSelector value={noteType} onChange={handleNoteTypeChange} />}
                <DialogForm.ItemList grid>
                    {/* Customer Field */}
                    <DialogForm.Item className="items-start">
                        <DialogForm.Label
                            htmlFor={customer ? undefined : 'customerId'}
                            icon={CustomerIcon}
                            required={!customer}
                        >
                            {t('notes__form__customer_label', { ns: 'notes' })}
                        </DialogForm.Label>
                        {customer ? (
                            <DialogForm.Body className="flex flex-col gap-1 py-1.5 px-2">
                                <Text color="neutral-950">{customer.customerName}</Text>
                            </DialogForm.Body>
                        ) : (
                            <DialogForm.Body className="flex flex-col gap-1">
                                <Controller
                                    name="customerId"
                                    control={control}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: t('form__customer_required', { ns: 'common' }),
                                        },
                                    }}
                                    render={({ field: { value, onChange } }) => (
                                        <CustomerSelect
                                            value={value}
                                            onChange={onChange}
                                            className="w-full"
                                            variant="inline"
                                            error={!!formErrors.customerId}
                                            tracking={{
                                                context: 'note_customer_select',
                                                context_entity_type: 'note',
                                            }}
                                        />
                                    )}
                                />
                                <DialogForm.Error error={formErrors.customerId} />
                            </DialogForm.Body>
                        )}
                    </DialogForm.Item>
                    {noteType !== 'NOTE' && (
                        <>
                            {event ? (
                                // Calendar-event entry: fixed, non-editable Termin row (no swap).
                                <DialogForm.Item className="items-start">
                                    <DialogForm.Label icon={CalendarIcon}>
                                        {t('notes__form__event_label', { ns: 'notes' })}
                                    </DialogForm.Label>
                                    <DialogForm.Body className="py-1.5 px-2">
                                        <Text color="neutral-950">{eventDateTime(event)}</Text>
                                    </DialogForm.Body>
                                </DialogForm.Item>
                            ) : (
                                // Customer entry: suggested (read-only + ✕) ↔ manual (Datum/Uhrzeit
                                // + chips), cross-faded with SwapSlot on dismiss / re-apply.
                                <>
                                    {showSuggestedEvent && (
                                        <div className="col-span-2 flex justify-end">
                                            <span
                                                className="text-xs font-medium"
                                                style={{
                                                    backgroundImage:
                                                        'linear-gradient(to right, #4C74E6, #8D54E9)',
                                                    WebkitBackgroundClip: 'text',
                                                    backgroundClip: 'text',
                                                    color: 'transparent',
                                                }}
                                            >
                                                {t('notes__form__last_event', { ns: 'notes' })}
                                            </span>
                                        </div>
                                    )}

                                    {/* Stack wrapper: passes the form's [auto_1fr] columns down
                                    via subgrid so both SwapSlots align with the other rows. */}
                                    <div className="col-span-2 grid grid-cols-subgrid">
                                        <SwapSlot subgrid active={showSuggestedEvent}>
                                            <DialogForm.Label icon={CalendarIcon}>
                                                {t('notes__form__event_label', { ns: 'notes' })}
                                            </DialogForm.Label>
                                            {/* `relative` anchors the AI prefill rail to this
                                            field cell (the SwapSlot's overflow-hidden would
                                            otherwise clip a rail anchored to a far ancestor). */}
                                            <DialogForm.Body className="relative py-1.5 px-2">
                                                <>
                                                    <span
                                                        aria-hidden
                                                        className="pointer-events-none absolute inset-y-0 right-0 w-1"
                                                        style={{
                                                            background:
                                                                'linear-gradient(180deg, #A5B4FC, #C4B5FD)',
                                                        }}
                                                    />
                                                    <Text color="neutral-950">
                                                        {eventDateTime(suggestedEvent ?? undefined)}
                                                    </Text>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        icon={X}
                                                        onClick={dismissSuggestion}
                                                        aria-label={t('notes__form__date_clear', {
                                                            ns: 'notes',
                                                        })}
                                                        className="h-auto min-w-0 rounded p-0.5 text-neutral-700 hover:text-neutral-950"
                                                    />
                                                </>
                                            </DialogForm.Body>
                                        </SwapSlot>
                                        <SwapSlot subgrid active={!showSuggestedEvent}>
                                            {/* Date Field */}
                                            <DialogForm.Label icon={CalendarIcon} required>
                                                {t('notes__form__date_label', { ns: 'notes' })}
                                            </DialogForm.Label>
                                            <div className="min-w-0">
                                                <Controller
                                                    name="occurredAt"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <DatePicker
                                                            disableIcon
                                                            mode="single"
                                                            value={
                                                                field.value
                                                                    ? new Date(field.value)
                                                                    : undefined
                                                            }
                                                            valuePrefix=""
                                                            onChange={(value) => {
                                                                if (!value) {
                                                                    return;
                                                                }

                                                                const baseDate = field.value
                                                                    ? new Date(field.value)
                                                                    : new Date();

                                                                const timePart = formatISO(baseDate, {
                                                                    representation: 'time',
                                                                });
                                                                const datePart = formatISO(value, {
                                                                    representation: 'date',
                                                                });
                                                                const nextDate = new Date(
                                                                    datePart + 'T' + timePart,
                                                                );

                                                                field.onChange(
                                                                    min([
                                                                        getMaxDate(),
                                                                        nextDate,
                                                                    ]).toISOString(),
                                                                );
                                                            }}
                                                            disabled={{ after: new Date() }}
                                                            className="w-full border border-transparent py-1"
                                                        />
                                                    )}
                                                />
                                            </div>

                                            {/* Time Field */}
                                            <DialogForm.Label htmlFor={'businessTime'} icon={ClockIcon}>
                                                {t('notes__form__time_label', { ns: 'notes' })}
                                            </DialogForm.Label>
                                            <div className="min-w-0">
                                                <Controller
                                                    name="occurredAt"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TimePicker
                                                            value={
                                                                field.value
                                                                    ? format(
                                                                          new Date(field.value),
                                                                          'HH:mm:ss',
                                                                      )
                                                                    : null
                                                            }
                                                            onChange={(value) => {
                                                                const baseDate = field.value
                                                                    ? new Date(field.value)
                                                                    : new Date();

                                                                const datePart = formatISO(baseDate, {
                                                                    representation: 'date',
                                                                });
                                                                const nextDate = new Date(
                                                                    datePart + 'T' + value,
                                                                );

                                                                field.onChange(nextDate.toISOString());
                                                            }}
                                                            max={
                                                                !field.value || isToday(field.value)
                                                                    ? format(getMaxDate(), 'HH:mm:ss')
                                                                    : undefined
                                                            }
                                                            placeholder={t('time_picker__placeholder', {
                                                                ns: 'common',
                                                            })}
                                                            className="w-full border border-transparent py-1"
                                                        />
                                                    )}
                                                />
                                            </div>

                                            {/* Jetzt + letzter Termin chips */}
                                            {suggestedEvent && (
                                                <div className="col-span-2 flex flex-wrap items-center gap-2">
                                                    {suggestionDismissed && (
                                                        <Button
                                                            type="button"
                                                            variant="subtle"
                                                            rounded
                                                            onClick={applyNow}
                                                            className="h-auto gap-1.5 bg-neutral-100 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-200"
                                                        >
                                                            {t('notes__form__date_now', { ns: 'notes' })}
                                                        </Button>
                                                    )}
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        rounded
                                                        leftIcon={CalendarIcon}
                                                        onClick={reapplySuggestion}
                                                        className="h-auto gap-1.5 bg-violet-100 px-3 py-1.5 text-sm text-neutral-900 hover:bg-violet-200"
                                                    >
                                                        {t('notes__form__date_prefill_source', {
                                                            ns: 'notes',
                                                        })}
                                                    </Button>
                                                </div>
                                            )}
                                        </SwapSlot>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </DialogForm.ItemList>
                <DialogForm.Item className="items-center justify-between">
                    <DialogForm.Label icon={NotebookPen}>
                        {t('notes__form__protokoll_label', { ns: 'notes' })}
                    </DialogForm.Label>
                    <div className="flex gap-1">
                        {enableFileUpload && (
                            <Button
                                type="button"
                                variant="icon"
                                size="icon"
                                icon={PaperclipIcon}
                                onClick={fileUploader.open}
                                disabled={!fileUploader.isReady}
                                aria-label={t('attachments__add_files', { ns: 'common' })}
                            />
                        )}
                        <VoiceRecordingButton
                            onTranscription={(text) => {
                                const next = appendPlainTextToLexical(getValues('title'), text);
                                setValue('title', next, { shouldDirty: true });
                            }}
                        />
                    </div>
                </DialogForm.Item>
                {attachmentsField.field.value && attachmentsField.field.value.length > 0 && (
                    <div className="flex min-w-0 w-full flex-row flex-nowrap gap-2 overflow-x-auto pb-1">
                        {attachmentsField.field.value.map((file) => (
                            <div key={file.externalUuid} className="w-40 shrink-0">
                                <FileCard
                                    file={file}
                                    onRemove={fileUploader.remove}
                                    onEdit={fileUploader.edit}
                                    onDownload={fileUploader.download}
                                />
                            </div>
                        ))}
                    </div>
                )}
                {fileUploader.uploaderElement}
                {noteType === 'NOTE' ? (
                    <NoteFormFields
                        noteType={noteType}
                        control={control}
                        errors={formErrors}
                        onAIInteraction={handleAIInteraction}
                    />
                ) : (
                    <Controller
                        name="interactionHappened"
                        control={control}
                        render={({ field }) => {
                            const notReached = field.value === false;

                            return (
                                <div>
                                    {/* Reached → note input (collapses to a placeholder look
                                        when unfocused via focus-within) + "Nicht angetroffen" pill. */}
                                    <SwapSlot active={!notReached} transition="duration-200 ease-out">
                                        <div className="flex flex-col gap-1.5">
                                            <NoteFormFields
                                                noteType={noteType}
                                                control={control}
                                                errors={formErrors}
                                                onAIInteraction={handleAIInteraction}
                                                disableVoiceRecording={true}
                                                variant="inline"
                                            />
                                            <Button
                                                type="button"
                                                variant="subtle"
                                                rounded
                                                leftIcon={BanIcon}
                                                onClick={() => {
                                                    field.onChange(false);
                                                }}
                                                className="h-auto gap-1.5 self-start bg-neutral-100 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-200"
                                            >
                                                {t('notes__not_reached__active_label', { ns: 'notes' })}
                                            </Button>
                                        </div>
                                    </SwapSlot>
                                    {/* Not reached → removable label (✕ returns the note input). */}
                                    <SwapSlot active={notReached} transition="duration-200 ease-out">
                                        <div className="flex items-center gap-1 px-2 py-1">
                                            <Text color="neutral-950">
                                                {t('notes__not_reached__active_label', { ns: 'notes' })}
                                            </Text>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                onClick={() => {
                                                    field.onChange(true);
                                                }}
                                                aria-label={t('notes__not_reached__back', { ns: 'notes' })}
                                                className="h-auto min-w-0 rounded p-1 text-neutral-700 hover:text-neutral-950"
                                            >
                                                <X className="h-3.5 w-3.5" />
                                            </Button>
                                        </div>
                                    </SwapSlot>
                                </div>
                            );
                        }}
                    />
                )}
                {noteType !== 'NOTE' && (
                    <LogInteractionTaskList
                        tasks={upcomingTasksQuery.data ?? []}
                        taskUpdates={watchedTaskUpdates}
                        onToggleCompleted={handleToggleCompleted}
                    />
                )}
                {noteType !== 'NOTE' && enableCreateNextEventFakeDoor && (
                    <div className="self-start">
                        <Button variant="secondary" icon={CalendarPlus} onClick={handleCreateNextEventClick}>
                            {t('fake_door_next_event_button', { ns: 'events' })}
                        </Button>
                    </div>
                )}
            </DialogForm.Content>
            <DialogForm.SubmitBlock
                onCancel={onCancel}
                onSubmit={() => {
                    void onSubmit();
                }}
                submitText={notReachedMode ? t('notes__not_reached__submit', { ns: 'notes' }) : undefined}
                disabled={createMutation.isPending || isAIBusy || !!currentSessionId}
            />
        </DialogForm>
    );
};

type LogInteractionDialogProps = ModalInnerProps & LogInteractionFormProps;

export const LogInteractionDialog = DialogStateManager.create<LogInteractionDialogProps>(
    ({ onSuccess, ...props }) => {
        const { visible, remove } = useDialog();
        const { enableUnifiedLogInteraction } = useAppConfig();
        const [isDirty, setIsDirty] = useState(false);
        const { confirmDiscard } = useDiscardChangesWarning(isDirty);

        const handleClose = (): void => {
            confirmDiscard(remove);
        };

        const Form = enableUnifiedLogInteraction ? LogInteractionForm : CreateNoteForm;

        return (
            <Dialog open={visible} showClose onClose={handleClose}>
                <Form
                    onCancel={handleClose}
                    onSuccess={() => {
                        remove();
                        onSuccess?.();
                    }}
                    onIsDirtyChange={setIsDirty}
                    {...props}
                />
            </Dialog>
        );
    },
);
